import { environments } from 'environments/environment';
import { ajax } from '@setel/webcommon/exposes/ajax';
import {
  I2FALoginResponse,
  IamNamespaces,
  RefreshSession,
} from 'shared/consts/auth.const';
import { ILoginResponse } from 'shared/consts/auth.const';
import { setAccessTokenInHeader } from 'modules/auth/auth.service';
import { IdentityTypesEnum } from './api-accounts.service';
import {
  axiosWithoutRefreshToken,
  filterEmptyString,
  IPaginationResult,
} from 'lib/ajax-helper';
import { ILoyaltyCard } from 'shared/consts/loyalty.const';
import {
  ITopupFilter,
  ITransaction,
} from 'shared/interfaces/transaction.interface';

const BASE_URL = `${environments.apiOpsBaseUrl}/api/iam`;
const IDP_API_BASE_URL = `${environments.apiOpsBaseUrl}/api/idp`;
const PAYMENTS_API_BASE_URL = `${environments.apiOpsBaseUrl}/api/payments`;
const LOYALTY_API_BASE_URL = `${environments.apiOpsBaseUrl}/api/loyalty`;

export function login(
  identifier: string,
  password: string,
  namespace: IamNamespaces
) {
  return axiosWithoutRefreshToken
    .post<ILoginResponse | I2FALoginResponse>(
      `${BASE_URL}/${namespace}/auth/login`,
      { identifier, password }
    )
    .then((res) => res.data);
}

export async function refreshToken(
  refreshToken: string,
  namespace: IamNamespaces
) {
  const url = `${BASE_URL}/${namespace}/auth/refresh-token`;
  const res = await axiosWithoutRefreshToken.post<RefreshSession>(url, {
    refreshToken,
  });
  setAccessTokenInHeader(res.data.accessToken);

  return res.data;
}

export async function indexUsers({ search, ...filter }: ICustomerRequest) {
  if (search?.phone?.startsWith('0')) {
    search.phone = '6' + search.phone;
  }

  const url = `${IDP_API_BASE_URL}/profiles`;
  const options = filterEmptyString({ ...filter, ...search });
  return ajax<IPaginationResult<any>>({
    url,
    params: {
      ...options,
    },
    select: ({ data, headers }) => {
      const items = data.data || [];
      return {
        items,
        isEmpty: items.length === 0,
        page: +headers['x-page'] || 0,
        perPage: +headers['x-per-page'] || 0,
        pageCount: +headers['x-pages-count'] || 0,
        total: +headers['x-total-count'] || 0,
      };
    },
  });
}

export const indexUserLoyaltyCards = (userId: string) =>
  ajax.get<ILoyaltyCard>(`${LOYALTY_API_BASE_URL}/system/cards/${userId}`);

interface IRequest {
  perPage?: number;
  page?: number;
  sortDate?: 'asc' | 'desc';
}

export interface SearchUserOptions {
  email?: string;
  phone?: string;
}

export interface ICustomerRequest extends IRequest {
  search?: SearchUserOptions;
}

export interface ICustomerLite {
  id: string;
  userId: string;
  name: string;
  email: string;
  phone: string;
}

export interface ICustomerWalletInfo {
  id: string;
  balance: number;
  isCreated?: boolean;
  limit?: number;
}

export interface ICustomerRefreshBalanceResponse {
  id: string;
  balance: number;
}

export interface ICustomer extends ICustomerLite {
  tierTitle: string;
  referralCode: string;
  referrerCode?: any;
  isBonusGranted: boolean;
  isBonusUnlocked: boolean;
  createdAt: Date;
  updatedAt: Date;
  deviceId: string;
  verified: boolean;
  internal: boolean;
  isEmailVerified: boolean;
  isEnabled: boolean;
  identityType?: IdentityTypesEnum;
  identityNumber?: string;
  language?: string;
  walletLimit?: number;
}

export interface IUserWalletParams {
  balance: number;
  id?: string;
  isCreated: boolean;
  limit: number;
}

export interface I2FALoginInput {
  authenticityToken: string;
  otp: string;
  identifier: string;
}

export function indexCustomerTransactions(
  userId: string,
  filter: ITopupFilter
) {
  return ajax.get<ITransaction[]>(
    `${PAYMENTS_API_BASE_URL}/admin/transactions?userId=${userId}&types[]=topup&types[]=refund`,
    {
      params: filter,
    }
  );
}

export const getUserWallet = (userId: string) => {
  const url = `${PAYMENTS_API_BASE_URL}/admin/users/${userId}/wallet`;
  return ajax.get<IUserWalletParams>(url);
};

export const login2FA = (
  input: I2FALoginInput,
  namespace: IamNamespaces = IamNamespaces.SETEL_ADMINS
) => {
  return ajax.post<ILoginResponse>(
    `${BASE_URL}/${namespace}/auth/2fa/login`,
    input
  );
};
